import { Address, AddressTemplate, TLabelSelectedItem } from '@api/apiClient';
import { Control } from 'react-hook-form';
import { IPlacingOrderButton } from '@components/PlacingOrderButtons/types';
import { TPlacingOrderListItem } from '@components/PlacingOrderView/types';
import {
  TAddress,
  TCitySuggestion,
  TDistrict,
  TRegion,
  TSuggestionTR,
  TSuggestionTRByIndex,
  TValidationInfo
} from '@api/apiClient/address';
import { TSetState } from '@src/types.ts';

export enum EPlacingOrder {
  FBP_NEW = 'fbp-new',
  PICKUP_READY = 'pickUp-ready',
  PROBLEMATIC_CUSTOMS = 'problematic-customs',
  PROBLEMATIC_FIRST_MILE = 'problematic-firstMile',
  RETURN_AWAITING = 'return-awaiting',
  UNCLAIMED_AWAITING = 'unclaimed-awaiting',
  UNCLAIMED_UNPAID = 'unclaimed-unpaid'
}

export type TPlacingOrderDataItem = {
  address: Address | undefined;
  addressEnglish?: Address;
  comment?: string;
  email?: string;
  fullName: string | undefined;
  id: string;
  phone: string | undefined;
  rememberAddress: boolean;
  shipmentDate?: Date;
};

export type TPlacingOrderData = TPlacingOrderDataItem[];

export interface IPlacingOrderModalWindowProps {
  availableCountries?: string[];
  costOfService: number;
  costOfServiceDifferent?: number;
  deliveryType?: string;
  errorSendPlacingOrder: string | null | unknown;
  errorSendTopUpBalance: string | null | unknown;
  expandTabs?: boolean;
  handlerOnClosePlacingOrderProcessing: () => void;
  handlerResetSendOrder: () => void;
  handlerSetDeliveryType?: TSetState<string>;
  handlerSubmit: (forms: TPlacingOrderData) => void;
  isErrorSendPlacingOrder: boolean;
  isErrorSendTopUpBalance: boolean;
  isLoadingSendPlacingOrder: boolean;
  isLoadingSendTopUpBalance: boolean;
  isNew?: boolean;
  parcelsAny?: TPlacingOrderListItem[] | undefined;
  parcelsGood: TPlacingOrderListItem[] | undefined;
  parcelsGoodTimeslotFrom?: Date | undefined;
  parcelsGoodWarehouse?: string;
  placingOrderInProcessed?: boolean;
  placingOrderType: EPlacingOrder;
  allowRu?: boolean;
}

export type TPlacingOrderShipmentDates = {
  selected: Date | undefined;
  values: Date[]
};

export type TPlacingOrderFormSelf = {
  addressTemplates: AddressTemplate[];
  availableCountries?: string[];
  contactHint: string | undefined;
  contactMinLength: number | undefined;
  contactPlaceholder: string;
  description: string | string[] | React.ReactNode | React.ReactNode[] | undefined;
  isNew: boolean | undefined;
  regions: TRegion[];
  rememberAddress: boolean;
  shipmentDates: TPlacingOrderShipmentDates;
  showComment: boolean;
  showEmail: boolean;
  showOneCountryPhone: boolean;
  title: string;
  values: AddressTemplate | undefined;
  withDateButtons: boolean;
  withDatePicker: boolean;
  withFullAddressInEnglish: boolean;
  [key: string]: any;
};

export type TPlacingOrderForm = [string, TPlacingOrderFormSelf];

export type TUpdateFormValue = boolean | Date | AddressTemplate;

export interface IPlacingOrderDatePickerProps {
  country: string;
  handlerUpdateForm: (country: string, propertyPath: string, value: TUpdateFormValue) => void;
  isNew: boolean | undefined;
  shipmentDates: TPlacingOrderShipmentDates;
  withDateButtons: boolean;
  withDatePicker: boolean;
}

export interface IPlacingOrderTitleProps {
  title: string;
}

export interface IPlacingOrderFormProps {
  addressTemplates: AddressTemplate[];
  availableCountries?: string[];
  buttons: IPlacingOrderButton[];
  contactHint: string | undefined;
  contactMinLength: number | undefined;
  contactPlaceholder: string;
  country: string;
  currentStep: number;
  dataGetValidation: TValidationInfo | undefined;
  description: string | string[] | React.ReactNode | React.ReactNode[] | undefined;
  handlerMutateGetValidation: (address: { address: string }) => void;
  handlerOnClickDeleteAddress: (id: string) => void;
  handlerUpdateForm: (country: string, propertyPath: string, value: TUpdateFormValue) => void;
  isNew: boolean | undefined;
  numberOfSteps: number | undefined;
  regions: TRegion[];
  rememberAddress: boolean;
  shipmentDates: TPlacingOrderShipmentDates;
  showComment: boolean;
  showEmail: boolean;
  showOneCountryPhone: boolean;
  title: string;
  values?: AddressTemplate | undefined;
  withDateButtons: boolean;
  withDatePicker: boolean;
  withFullAddressInEnglish: boolean;
}

export interface IPlacingOrderAddressDropdownItemProps {
  item: TLabelSelectedItem;
  handlerGetFullAddress: (item: TLabelSelectedItem, from?: string) => string;
  handlerOnClickDeleteAddress: (id: string) => void;
}

export interface IPlacingOrderAddressEnglishProps {
  control: Control<AddressTemplate>;
}

export interface IPlacingOrderContactsProps {
  contactHint: string | undefined;
  contactMinLength: number | undefined;
  contactPlaceholder: string;
  control: Control<AddressTemplate>;
  country?: string | undefined;
  showEmail?: boolean;
}

export type TUseSuggestionsCnResult = TUseSuggestionsResult<TAddress>;
export type TUseSuggestionsCnCityResult = TUseSuggestionsResult<TCitySuggestion>;
export type TUseDistrictSuggestionsResult = TUseSuggestionsResult<TDistrict>;

export interface IPlacingOrderAddressFieldsFirstProps {
  availableCountries?: string[];
  control: Control<AddressTemplate>;
  country: string;
  emptyAddressWithUpdate: AddressTemplate;
  fullListAddresses: AddressTemplate[];
  handlerGetFullAddress: (item: TLabelSelectedItem, from?: string) => string;
  handlerOnClickDeleteAddress: (id: string) => void;
  handlerSetCurrentAddress: (id: string) => void;
  handlerSetSelectedSuggestion: TSetState<TAddress | undefined>;
  labelSelectedItem: TLabelSelectedItem;
  region: string | undefined;
  regions: TRegion[];
  useSuggestions: (search: string) => TUseSuggestionsCnResult;
  withFullAddressInEnglish: boolean;
}

export interface IPlacingOrderAddressHKProps {
  availableCountries?: string[];
  control: Control<AddressTemplate>;
  country: string;
  emptyAddressWithUpdate: AddressTemplate;
  fullListAddresses: AddressTemplate[];
  handlerGetFullAddress: (item: TLabelSelectedItem, from?: string) => string;
  handlerOnClickDeleteAddress: (id: string) => void;
  handlerSetCurrentAddress: (id: string) => void;
  handlerSetSelectedSuggestion: TSetState<TAddress | undefined>;
  labelSelectedItem: TLabelSelectedItem;
  region: string | undefined;
  cityDistrict: string | undefined;
  useSuggestions: (search: string) => TUseSuggestionsCnResult;
  useAddressSuggestionsHKDistrict: (search: string) => TUseDistrictSuggestionsResult;
  withFullAddressInEnglish: boolean;
}

export interface IPlacingOrderAddressFieldsCNProps {
  availableCountries?: string[];
  control: Control<AddressTemplate>;
  country: string;
  emptyAddressWithUpdate: AddressTemplate;
  fullListAddresses: AddressTemplate[];
  handlerGetFullAddress: (item: TLabelSelectedItem, from?: string) => string;
  handlerOnClickDeleteAddress: (id: string) => void;
  handlerSetCurrentAddress: (id: string) => void;
  handlerSetSelectedSuggestion: TSetState<TAddress | undefined>;
  labelSelectedItem: TLabelSelectedItem;
  region: string | undefined;
  regions: TRegion[];
  useSuggestions: (search: string) => TUseSuggestionsCnCityResult;
  useAddressSuggestionsForCity: (search: string) => TUseSuggestionsCnCityResult;
  useAddressSuggestionsForCityDistrict: (search: string) => TUseSuggestionsCnResult;
  withFullAddressInEnglish: boolean;
  isMunicipality: boolean;
}

export type TUseSuggestionsResult<T> = {
  isLoading: boolean;
  isFetching: boolean;
  isFetched: boolean;
  isError: boolean;
  data?: T[] | undefined;
  refetch: (options?: any) => Promise<any>;
}

export type TUseSuggestionsTrResult = TUseSuggestionsResult<TSuggestionTR>

export type TUseSuggestionsByIndexTrResult = TUseSuggestionsResult<TSuggestionTRByIndex>

export interface IPlacingOrderAddressFieldsSecondProps {
  city: string | undefined;
  control: Control<AddressTemplate>;
  emptyAddressWithUpdate: AddressTemplate;
  fullListAddresses: AddressTemplate[];
  handlerGetFullAddress: (item: TLabelSelectedItem, from?: string) => string;
  handlerOnClickDeleteAddress: (id: string) => void;
  handlerSetCurrentAddress: (id: string) => void;
  handlerSetSelectedSuggestion: TSetState<TSuggestionTR | undefined>;
  handlerSetSelectedSuggestionByIndex: TSetState<TSuggestionTRByIndex | undefined>;
  labelSelectedItem: TLabelSelectedItem;
  street: string | undefined;
  useSuggestions: (search: string) => TUseSuggestionsTrResult;
  useSuggestionsByIndex: (search: string) => TUseSuggestionsByIndexTrResult;
  withFullAddressInEnglish: boolean;
  zipCode: string | undefined;
}

export interface IPlacingOrderActionProps {
  buttons: IPlacingOrderButton[];
  description?: string;
  title: string;
}

export interface IPlacingOrderSelectingDeliveryProps {
  buttons: IPlacingOrderButton[];
  costOfService: number;
  costOfServiceDifferent: number;
  deliveryType: string;
  handlerSetDeliveryType: TSetState<string>;
  allowRu?: boolean;
}

export type TSelectingDeliveryActions = {
  cost: string;
  description: string;
  title: string;
  type: string;
}

export interface IPlacingOrderAddressFieldsThirdProps {
  control: Control<AddressTemplate>;
  country: string;
  emptyAddressWithUpdate: AddressTemplate;
  fullListAddresses: AddressTemplate[];
  handlerGetFullAddress: (item: TLabelSelectedItem, from?: string) => string;
  handlerOnClickDeleteAddress: (id: string) => void;
  handlerSetCurrentAddress: (id: string) => void;
  labelSelectedItem: TLabelSelectedItem;
  withFullAddressInEnglish: boolean;
  handlerSetSelectedSuggestion: (s: string) => void;
}
